import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { lumi, isDemoMode } from '../lib/lumi';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

type Status = { testMode: boolean; active: boolean; status: string; until: number | null; price: { amount: number; currency: string } };
export default function AssinaturaTeste() {
  const { user, isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [content, setContent] = useState('');
  const lock = useRef(false);
  const generation = useRef(0);
  const currentUser = useRef(user?.userId);
  currentUser.current = user?.userId;
  const request = async (action: 'status' | 'checkout' | 'member_content') => {
    if (isDemoMode || !user?.userId || !isAuthenticated || lock.current) return;
    const owner = user.userId;
    const requestGeneration = generation.current;
    const stillCurrent = () => currentUser.current === owner && generation.current === requestGeneration;
    lock.current = true; setBusy(true); setError(''); setContent(''); setStatus(null);
    try {
      const token = lumi.auth.accessToken;
      if (!token) throw new Error('Sua sessão não está disponível. Entre novamente com sua conta de teste.');
      const authorization = `Bearer ${token}`;
      const response = await fetch('https://api.lumi.new/v1/functions/p429616054542557184/stripe-subscription-test', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: authorization }, body: JSON.stringify({ action }),
      });
      const data = await response.json();
      if (!stillCurrent()) return;
      if (!response.ok) throw new Error(data.error || 'Não foi possível consultar a assinatura de teste.');
      if (data.testMode !== true) throw new Error('Resposta inesperada. O pagamento de teste foi bloqueado.');
      if (action === 'checkout') {
        const url = new URL(data.url);
        if (url.origin !== 'https://checkout.stripe.com') throw new Error('Endereço de pagamento inválido.');
        window.location.assign(url.href);
      } else {
        setStatus(data);
        if (action === 'member_content' && data.active === true) setContent(data.content);
      }
    } catch (e) { if (stillCurrent()) setError(e instanceof Error ? e.message : 'Falha de conexão. Tente novamente.'); }
    finally { lock.current = false; if (stillCurrent()) setBusy(false); }
  };
  useEffect(() => {
    generation.current += 1; setStatus(null); setContent(''); setError(''); setBusy(false);
    return () => { generation.current += 1; };
  }, [user?.userId]);
  const available = !isDemoMode && isAuthenticated && Boolean(user?.userId);
  return <><Navbar /><main className="min-h-[75vh] bg-[#171717] text-white px-4 py-12">
    <div className="max-w-xl mx-auto rounded-2xl border border-amber-400/40 p-6 sm:p-8 space-y-5">
      <p className="text-amber-300 font-semibold">Stripe Sandbox · somente testes</p>
      <h1 className="text-3xl font-bold">Assinatura do trabalhador</h1>
      <p className="text-gray-300">Teste o pagamento e a confirmação da assinatura com um cartão fictício.</p>
      {isDemoMode ? <p className="text-amber-200">Saia da demonstração e entre com um cadastro de trabalhador autorizado para este teste. Alex e Brasa são perfis locais e não acessam o Stripe.</p> : !available ? <Link className="underline" to="/cadastro">Entrar com minha conta de teste</Link> : <>
        {params.get('retorno') === 'sucesso' && <p>Você voltou do checkout. Clique em Consultar assinatura para confirmar o pagamento no Stripe.</p>}
        {params.get('retorno') === 'cancelado' && <p>Você voltou sem concluir este checkout. Consulte o status antes de tentar novamente.</p>}
        {status && <div className="rounded-xl bg-[#282828] p-4 space-y-2">
          <p>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: status.price.currency }).format(status.price.amount / 100)} por mês — valor fictício</p>
          <p role="status">{status.active ? 'Assinatura de teste ativa e paga.' : 'Sem assinatura de teste ativa e paga.'}</p>
          {status.until && <p>Período confirmado até {new Date(status.until * 1000).toLocaleDateString('pt-BR')}.</p>}
        </div>}
        <div className="flex flex-col gap-3">
          <button disabled={busy} onClick={() => request('status')} className="border border-gray-500 rounded-xl px-5 py-3 disabled:opacity-50">{busy ? 'Consultando…' : 'Consultar assinatura'}</button>
          <button disabled={busy || !status || status.active} onClick={() => request('checkout')} className="bg-[#E53935] rounded-xl px-5 py-3 font-semibold disabled:opacity-50">Iniciar assinatura fictícia</button>
          {status?.active && <button disabled={busy} onClick={() => request('member_content')} className="bg-green-800 rounded-xl px-5 py-3">Abrir área de teste</button>}
        </div>
        <p className="text-xs text-gray-400">Primeiro consulte a assinatura para carregar o preço do Sandbox. Este teste não modifica as permissões de vagas ou currículos do site.</p>
      </>}
      {error && <p role="alert" className="text-red-300">{error}</p>}
      {content && <p role="status" className="rounded-xl border border-green-500 p-4">{content}</p>}
      <div className="text-sm text-gray-300 border-t border-gray-700 pt-4">Cartão de teste: <strong>4242 4242 4242 4242</strong><br />Validade: 12/34 · CVC: 123<br />Use somente no Sandbox.</div>
      <Link className="inline-block underline" to="/perfil">Voltar ao perfil</Link>
    </div>
  </main><Footer /></>;
}
